import { useEffect, useRef, useState } from 'react';

interface Card {
  title: string;
  description?: string;
  gradeTag?: string;
  gradeTagColor?: string;
  image?: string;
  schoolName?: string;
  schoolLogo?: string;
  region?: string;
  link?: string;
}

interface ResponsiveSize {
  desktop: string;
  tablet: string;
  mobile: string;
}

interface Props {
  cards?: Card[];

  // Title
  titleFontFamily?: string;
  titleFontSize?: ResponsiveSize;
  titleFontWeight?: string;
  titleFontStyle?: string;
  titleColor?: string;

  // Description
  descriptionFontFamily?: string;
  descriptionFontSize?: ResponsiveSize;
  descriptionFontWeight?: string;
  descriptionFontStyle?: string;
  descriptionColor?: string;

  // Dropdown / filters
  dropdownFontFamily?: string;
  dropdownFontSize?: ResponsiveSize;
  dropdownFontWeight?: string;
  dropdownFontStyle?: string;
  dropdownColor?: string;

  // School name
  schoolNameFontFamily?: string;
  schoolNameFontSize?: ResponsiveSize;
  schoolNameFontWeight?: string;
  schoolNameFontStyle?: string;
  schoolNameColor?: string;

  // New filter options
  filterButtonSize?: ResponsiveSize; // padding: "0.4rem 1rem"
  filterFontSize?: ResponsiveSize;
}

function useDeviceType() {
  const [device, setDevice] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    const checkDevice = () => {
      if (window.innerWidth <= 768) setDevice('mobile');
      else if (window.innerWidth <= 1024) setDevice('tablet');
      else setDevice('desktop');
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  return device;
}

function getResponsiveValue(value: ResponsiveSize | string | undefined, device: 'desktop' | 'tablet' | 'mobile') {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  return value[device];
}

export function CaseStudyCardGrid({
  cards = [],

  titleFontFamily = 'GT-Walsheim-Pro-Regular',
  titleFontSize = { desktop: '2.1rem', tablet: '1.8rem', mobile: '1.5rem' },
  titleFontWeight = 'bold',
  titleFontStyle = 'normal',
  titleColor = '#000',

  descriptionFontFamily = 'GT-Walsheim-Pro-Regular',
  descriptionFontSize = { desktop: '1.4rem', tablet: '1.2rem', mobile: '1rem' },
  descriptionFontWeight = 'normal',
  descriptionFontStyle = 'normal',
  descriptionColor = '#333',

  dropdownFontFamily = 'GT-Walsheim-Pro-Regular',
  dropdownFontSize = { desktop: '1rem', tablet: '0.9rem', mobile: '0.85rem' },
  dropdownFontWeight = 'normal',
  dropdownFontStyle = 'normal',
  dropdownColor = '#000',

  schoolNameFontFamily = 'GT-Walsheim-Pro-Regular',
  schoolNameFontSize = { desktop: '1.5rem', tablet: '1.3rem', mobile: '1.1rem' },
  schoolNameFontWeight = '600',
  schoolNameFontStyle = 'normal',
  schoolNameColor = '#000',

  filterButtonSize = { desktop: '0.4rem 1rem', tablet: '0.35rem 0.9rem', mobile: '0.3rem 0.8rem' },
  filterFontSize = { desktop: '1rem', tablet: '0.9rem', mobile: '0.85rem' },
}: Props) {
  const device = useDeviceType();
  const [visibleCount, setVisibleCount] = useState(9);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);
  const [regionDropdownOpen, setRegionDropdownOpen] = useState(false);
  const [gradeDropdownOpen, setGradeDropdownOpen] = useState(false);
  const regionDropdownRef = useRef<HTMLDivElement>(null);
  const gradeDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (regionDropdownRef.current && !regionDropdownRef.current.contains(event.target as Node)) {
        setRegionDropdownOpen(false);
      }
      if (gradeDropdownRef.current && !gradeDropdownRef.current.contains(event.target as Node)) {
        setGradeDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const regions = Array.from(new Set(cards.map((card) => card.region).filter(Boolean)));
  const grades = Array.from(new Set(cards.map((card) => card.gradeTag).filter(Boolean)));

  const filteredCards = cards.filter((card) => {
    const regionMatch = !selectedRegion || card.region === selectedRegion;
    const gradeMatch = !selectedGrade || card.gradeTag === selectedGrade;
    return regionMatch && gradeMatch;
  });

  const visibleCards = filteredCards.slice(0, visibleCount);

  // Styles
  const resolvedFilterPadding = getResponsiveValue(filterButtonSize, device);
  const resolvedFilterFontSize = getResponsiveValue(filterFontSize, device);

  const buttonStyle = (active: boolean) => ({
    padding: resolvedFilterPadding,
    border: 'none',
    backgroundColor: active ? '#00AEEF' : '#E7F8FB',
    borderRadius: '6px',
    fontSize: resolvedFilterFontSize,
    fontFamily: dropdownFontFamily,
    fontStyle: dropdownFontStyle,
    fontWeight: dropdownFontWeight,
    color: active ? 'white' : dropdownColor,
    cursor: 'pointer',
    minWidth: '100px',
  });

  const dropdownMenuStyle = (buttonRef: HTMLButtonElement | null) => ({
    position: 'absolute' as const,
    top: buttonRef ? buttonRef.offsetHeight : 0,
    left: 0,
    zIndex: 10,
    background: 'white',
    border: '1px solid #ccc',
    borderRadius: '4px',
    width: '100%',
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    fontFamily: dropdownFontFamily,
    fontSize: getResponsiveValue(dropdownFontSize, device),
    fontWeight: dropdownFontWeight,
    fontStyle: dropdownFontStyle,
    color: dropdownColor,
  });

  const dropdownItemStyle = {
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  };

  return (
    <div style={{ fontFamily: 'sans-serif', padding: '2rem' }}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
        <button
          style={buttonStyle(!selectedRegion && !selectedGrade)}
          onClick={() => {
            setSelectedRegion(null);
            setSelectedGrade(null);
            setVisibleCount(9);
          }}
        >
          All
        </button>

        {/* Region Dropdown */}
        <div style={{ position: 'relative' }} ref={regionDropdownRef}>
          <button
            style={buttonStyle(!!selectedRegion || regionDropdownOpen)}
            onClick={() => {
              setRegionDropdownOpen((prev) => !prev);
              setGradeDropdownOpen(false);
            }}
          >
            {selectedRegion || 'Region'} ▾
          </button>
          {regionDropdownOpen && (
            <div style={dropdownMenuStyle(regionDropdownRef.current?.querySelector('button'))}>
              {regions.map((region) => (
                <div
                  key={region}
                  style={dropdownItemStyle}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.backgroundColor = '#f0f0f0')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.backgroundColor = 'white')}
                  onClick={() => {
                    setSelectedRegion(region);
                    setRegionDropdownOpen(false);
                    setVisibleCount(9);
                  }}
                >
                  {region}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Grade Dropdown */}
        <div style={{ position: 'relative' }} ref={gradeDropdownRef}>
          <button
            style={buttonStyle(!!selectedGrade || gradeDropdownOpen)}
            onClick={() => {
              setGradeDropdownOpen((prev) => !prev);
              setRegionDropdownOpen(false);
            }}
          >
            {selectedGrade || 'Grade Level'} ▾
          </button>
          {gradeDropdownOpen && (
            <div style={dropdownMenuStyle(gradeDropdownRef.current?.querySelector('button'))}>
              {grades.map((grade) => (
                <div
                  key={grade}
                  style={dropdownItemStyle}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLDivElement).style.backgroundColor = '#f0f0f0')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLDivElement).style.backgroundColor = 'white')}
                  onClick={() => {
                    setSelectedGrade(grade);
                    setGradeDropdownOpen(false);
                    setVisibleCount(9);
                  }}
                >
                  {grade}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Card Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns:
            device === 'mobile' ? 'repeat(1, 1fr)' : device === 'tablet' ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
          gap: device === 'mobile' ? '1rem' : '1.5rem',
        }}
      >
        {visibleCards.map((card, index) => {
          const handleClick = () => {
            if (card.link) window.open(card.link, '_blank');
          };

          return (
            <div
              key={index}
              onClick={handleClick}
              style={{
                background: '#fff',
                borderRadius: '8px',
                padding: '1rem',
                boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
                minHeight: '100%',
                cursor: card.link ? 'pointer' : 'default',
              }}
            >
              {card.image && (
                <img src={card.image} alt={card.title} style={{ width: '100%', height: 'auto', borderRadius: '6px' }} />
              )}
              {card.gradeTag && (
                <div
                  style={{
                    backgroundColor: card.gradeTagColor || '#FF6B00',
                    color: 'white',
                    fontWeight: 'bold',
                    padding: '0.2rem 0.6rem',
                    borderRadius: '4px',
                    fontSize: '1.5rem',
                    marginTop: '1.3rem',
                    display: 'inline-block',
                  }}
                >
                  {card.gradeTag}
                </div>
              )}
              <div
                style={{
                  fontFamily: titleFontFamily,
                  fontWeight: titleFontWeight,
                  fontStyle: titleFontStyle,
                  fontSize: getResponsiveValue(titleFontSize, device),
                  color: titleColor,
                  marginTop: '1rem',
                }}
              >
                {card.title}
              </div>
              {card.description && (
                <div
                  style={{
                    fontFamily: descriptionFontFamily,
                    fontWeight: descriptionFontWeight,
                    fontStyle: descriptionFontStyle,
                    fontSize: getResponsiveValue(descriptionFontSize, device),
                    color: descriptionColor,
                    marginTop: '1rem',
                  }}
                  dangerouslySetInnerHTML={{ __html: card.description }}
                />
              )}

              {card.schoolName && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '1rem',
                    fontFamily: schoolNameFontFamily,
                    fontSize: getResponsiveValue(schoolNameFontSize, device),
                    fontWeight: schoolNameFontWeight,
                    fontStyle: schoolNameFontStyle,
                    color: schoolNameColor,
                  }}
                >
                  {card.schoolLogo && (
                    <img
                      src={card.schoolLogo}
                      alt='school logo'
                      style={{ width: '24px', height: '24px', borderRadius: '50%', marginRight: '0.5rem' }}
                    />
                  )}
                  <span>{card.schoolName}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Load More */}
      {visibleCount < filteredCards.length && (
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={() => setVisibleCount((prev) => prev + 9)}
            style={{
              padding: '0.5rem 1.25rem',
              border: '2px solid #00AEEF',
              backgroundColor: 'white',
              color: '#00AEEF',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'all 0.2s ease',
            }}
          >
            Load More
          </button>
        </div>
      )}
    </div>
  );
}
